/**
 * The sort control (§B list header, §E.9 sort menu, §H.2).
 *
 * A quiet button ("By time" + chevD) opening a small glass menu of three
 * menuitemradio items, right-aligned under the button: enter 240 settle,
 * exit 168 leave. Arrow keys move, Home/End jump, Enter/Space choose, Esc and
 * a choice return focus to the button, Tab and a press outside close it.
 */
import { useCallback, useEffect, useId, useRef, useState, type JSX, type KeyboardEvent } from "react";
import { Button, Icon } from "@/design";
import type { SortKey } from "@/contracts/store";
import { COPY, SORT_BUTTON, SORT_ITEM, SORT_KEYS } from "./format";

type MenuState = "closed" | "open" | "closing";
const EXIT_MS = 168;

export interface SortMenuProps {
  value: SortKey;
  onChange: (s: SortKey) => void;
  reduced: boolean;
  disabled?: boolean;
}

export function SortMenu({ value, onChange, reduced, disabled }: SortMenuProps): JSX.Element {
  const [state, setState] = useState<MenuState>("closed");
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const timer = useRef(0);
  const menuId = useId();

  const button = () => wrapRef.current?.querySelector<HTMLButtonElement>("button[aria-haspopup]") ?? null;
  const items = () => Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? []);

  const close = useCallback(
    (returnFocus: boolean) => {
      window.clearTimeout(timer.current);
      if (reduced) setState("closed");
      else {
        setState((s) => (s === "open" ? "closing" : s));
        timer.current = window.setTimeout(() => setState("closed"), EXIT_MS);
      }
      if (returnFocus) wrapRef.current?.querySelector<HTMLButtonElement>("button[aria-haspopup]")?.focus();
    },
    [reduced],
  );

  const open = () => {
    window.clearTimeout(timer.current);
    setState("open");
  };

  // On open, focus the checked item.
  useEffect(() => {
    if (state !== "open") return;
    const list = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? []);
    (list.find((b) => b.getAttribute("aria-checked") === "true") ?? list[0])?.focus();
  }, [state]);

  // A press outside closes the menu without stealing focus back.
  useEffect(() => {
    if (state !== "open") return undefined;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && e.target instanceof Node && wrapRef.current.contains(e.target)) return;
      close(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [state, close]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const choose = (k: SortKey) => {
    if (k !== value) onChange(k);
    close(true);
  };

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const list = items();
    const i = list.findIndex((b) => b === document.activeElement);
    let next = -1;
    if (e.key === "ArrowDown") next = (i + 1) % list.length;
    else if (e.key === "ArrowUp") next = (i - 1 + list.length) % list.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = list.length - 1;
    else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close(true);
      return;
    } else if (e.key === "Tab") {
      close(false);
      return;
    }
    if (next >= 0) {
      e.preventDefault();
      list[next]?.focus();
    }
  };

  const onButtonKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if ((e.key === "ArrowDown" || e.key === "ArrowUp") && e.target === button() && state !== "open") {
      e.preventDefault();
      open();
    }
  };

  const shown = state !== "closed";
  return (
    <div ref={wrapRef} className="ld-loc-panel-sort" onKeyDown={onButtonKey}>
      <Button
        variant="quiet"
        size="sm"
        iconRight="chevD"
        aria-haspopup="menu"
        aria-expanded={state === "open"}
        aria-controls={shown ? menuId : undefined}
        disabled={disabled}
        className={state === "open" ? "ld-loc-panel-sortbtn is-open" : "ld-loc-panel-sortbtn"}
        onClick={() => (state === "open" ? close(true) : open())}
      >
        {SORT_BUTTON[value]}
      </Button>
      {shown && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={COPY.sortMenu}
          className={state === "closing" ? "ld-glass ld-loc-panel-menu is-closing" : "ld-glass ld-loc-panel-menu"}
          onKeyDown={onMenuKey}
        >
          {SORT_KEYS.map((k) => (
            <button
              key={k}
              type="button"
              role="menuitemradio"
              aria-checked={k === value}
              tabIndex={-1}
              className="ld-island__item ld-rake"
              onClick={() => choose(k)}
            >
              <span>{SORT_ITEM[k]}</span>
              <Icon name="check" size={16} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
